import {
  type AssessmentSlideOverCandidate,
  useAssessmentSlideOver,
} from "@components/campaigns/AssessmentSlideOver";
import {
  RECOMMENDATION_COLORS,
  RECOMMENDATION_LABELS,
} from "@components/campaigns/CandidateAssessmentSummary";
import { useReturnToPool } from "@components/campaigns/useReturnToPool";
import { CandidateReferenceBadge } from "@components/candidates/CandidateReferenceBadge";
import { RadarChart } from "@components/charts/RadarChart";
import { ScoreComparisonTable } from "@components/charts/ScoreComparisonTable";
import { QueryErrorState } from "@components/shared/QueryErrorState";
import { type ThresholdFailure, ThresholdFlag } from "@components/shared/ThresholdFlag";
import {
  CLEAR_ASSESSMENT_THRESHOLD_OVERRIDE,
  MOVE_CANDIDATE_STAGE,
  OVERRIDE_ASSESSMENT_THRESHOLD,
  SAVE_CANDIDATE_ASSESSMENT,
  SUGGEST_COMPETENCY_SCORE_EVIDENCE,
} from "@lib/graphql/mutations";
import { ASSESSMENT_STAGE_QUERY } from "@lib/graphql/queries";
import { useCampaignChannel } from "@lib/hooks/useCampaignChannel";
import { ChevronDown, ClipboardCheck, Grid3x3, Pencil } from "lucide-react";
import { useCallback, useState } from "react";
import { Link } from "react-router";
import { useMutation, useQuery } from "urql";
import { labelForBriefField } from "../blueprintTypes";
import { AssessmentForm } from "./AssessmentForm";
import { type AssessmentGuideData, AssessmentGuidePanel } from "./AssessmentGuidePanel";
import { StageBadge } from "./StageBadge";
import { StageDropdown, TerminalStageMoveControl } from "./StageDropdown";

interface AssessmentStageProps {
  campaignId: string;
}

interface BehaviouralAnchorData {
  id: string;
  evidenceText: string;
  sourceExcerpt: string | null;
  sourceArtefactType: string | null;
  sourceArtefactId: string | null;
  aiGenerated: boolean;
  createdAt: string;
}

interface CompetencyScoreData {
  id: string;
  score: number | null;
  evidence: string | null;
  notes: string | null;
  detailedNotes: string | null;
  capabilityDimension: { id: string; continuityId: string; name: string };
  behaviouralAnchors: BehaviouralAnchorData[];
}

interface AssessmentVersionData {
  id: string;
  overallScore: number | null;
  recommendation: string | null;
  overallSummary: string | null;
  strengths: string | null;
  concerns: string | null;
  culturalFitNotes: string | null;
  trajectoryNotes: string | null;
  riskNotes: string | null;
  versionNumber: number;
  effectiveFrom: string;
  assessor: { id: string; name: string };
  competencyScores: CompetencyScoreData[];
  thresholdPassing: boolean | null;
  thresholdPassingWithOverride: boolean | null;
  thresholdOverriddenAt: string | null;
  thresholdOverrideReason: string | null;
  thresholdOverriddenBy: { id: string; name: string } | null;
  thresholdFailures: ThresholdFailure[];
  uncoveredBriefFields: string[];
}

interface CampaignCandidateData {
  id: string;
  stage: string;
  candidate: {
    id: string;
    fullName: string;
    referenceNumber?: number;
    currentRoleTitle: string | null;
    currentCompany: string | null;
    location: string | null;
  };
  candidateAssessment: {
    id: string;
    currentVersion: AssessmentVersionData | null;
  } | null;
}

interface DimensionData {
  id: string;
  continuityId: string;
  name: string;
  description: string | null;
  idealScore: number | null;
}

function buildScoresMap(competencyScores: CompetencyScoreData[]): Record<string, number> {
  const map: Record<string, number> = {};
  for (const cs of competencyScores) {
    if (cs.score != null) map[cs.capabilityDimension.continuityId] = cs.score;
  }
  return map;
}

export function AssessmentStage({ campaignId }: AssessmentStageProps) {
  const [assessingId, setAssessingId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const [{ data, fetching, error }, reexecute] = useQuery({
    query: ASSESSMENT_STAGE_QUERY,
    variables: { id: campaignId },
  });
  const [{ fetching: savingAssessment }, saveAssessment] = useMutation(SAVE_CANDIDATE_ASSESSMENT);
  const [, moveStage] = useMutation(MOVE_CANDIDATE_STAGE);
  const [, suggestEvidence] = useMutation(SUGGEST_COMPETENCY_SCORE_EVIDENCE);
  const [, overrideThreshold] = useMutation(OVERRIDE_ASSESSMENT_THRESHOLD);
  const [, clearThresholdOverride] = useMutation(CLEAR_ASSESSMENT_THRESHOLD_OVERRIDE);
  const [suggestingEvidenceForId, setSuggestingEvidenceForId] = useState<string | null>(null);

  const handleOverrideThreshold = useCallback(
    async (versionId: string, reason: string) => {
      await overrideThreshold({ input: { candidateAssessmentVersionId: versionId, reason } });
    },
    [overrideThreshold],
  );
  const handleClearThresholdOverride = useCallback(
    async (versionId: string) => {
      await clearThresholdOverride({ input: { candidateAssessmentVersionId: versionId } });
    },
    [clearThresholdOverride],
  );

  const handleSuggestEvidence = useCallback(
    async (competencyScoreId: string) => {
      setSuggestingEvidenceForId(competencyScoreId);
      await suggestEvidence({ input: { competencyScoreId } });
      setSuggestingEvidenceForId(null);
    },
    [suggestEvidence],
  );

  const allCandidates: CampaignCandidateData[] = data?.campaign?.campaignCandidates ?? [];
  const activeCandidates = allCandidates.filter((cc) => cc.stage === "assessment");
  const movedOnCandidates = allCandidates.filter((cc) => cc.stage !== "assessment");
  const dimensions: DimensionData[] = data?.campaign?.roleBlueprint?.capabilityDimensions ?? [];

  const { open: openAssessment, slideOverElement: assessmentSlideOver } = useAssessmentSlideOver({
    candidates: allCandidates as unknown as AssessmentSlideOverCandidate[],
    dimensions,
  });
  const assessmentGuide: AssessmentGuideData | null = data?.campaign?.assessmentGuide ?? null;
  const matrixReady: boolean = (data?.campaign?.assessmentMatrix?.currentVersion ?? null) !== null;

  const refresh = useCallback(() => reexecute({ requestPolicy: "network-only" }), [reexecute]);
  useCampaignChannel(campaignId, refresh);

  const { openFor: openReturnToPool, dialog: returnToPoolDialog } = useReturnToPool(refresh);

  const handleSaveAssessment = async (
    campaignCandidateId: string,
    formData: {
      overallSummary: string;
      strengths: string;
      concerns: string;
      culturalFitNotes: string;
      trajectoryNotes: string;
      riskNotes: string;
      competencyScores: {
        capabilityDimensionId: string;
        score: number;
        evidence: string;
        notes: string;
        detailedNotes: string;
      }[];
    },
  ) => {
    await saveAssessment({
      input: {
        campaignCandidateId,
        overallSummary: formData.overallSummary || undefined,
        strengths: formData.strengths || undefined,
        concerns: formData.concerns || undefined,
        culturalFitNotes: formData.culturalFitNotes || undefined,
        trajectoryNotes: formData.trajectoryNotes || undefined,
        riskNotes: formData.riskNotes || undefined,
        competencyScores: formData.competencyScores.map((cs) => ({
          capabilityDimensionId: cs.capabilityDimensionId,
          score: cs.score,
          evidence: cs.evidence || undefined,
          notes: cs.notes || undefined,
          detailedNotes: cs.detailedNotes || undefined,
        })),
      },
    });
    setAssessingId(null);
    refresh();
  };

  const handleMove = async (campaignCandidateId: string, stage: string) => {
    await moveStage({ input: { campaignCandidateId, stage } });
    refresh();
  };

  if (fetching && allCandidates.length === 0)
    return <p className="text-sm text-text-muted">Loading assessments...</p>;

  const renderCandidate = (cc: CampaignCandidateData, movedPast: boolean) => {
    const assessment = cc.candidateAssessment?.currentVersion;
    const isExpanded = expandedId === cc.id;

    if (!movedPast && assessingId === cc.id) {
      return (
        <div key={cc.id} className="mb-3">
          <div className="mb-2 text-sm font-medium text-text-primary">
            {typeof cc.candidate.referenceNumber === "number" && (
              <>
                <CandidateReferenceBadge referenceNumber={cc.candidate.referenceNumber} />{" "}
              </>
            )}
            {cc.candidate.fullName}
          </div>
          <AssessmentForm
            dimensions={dimensions}
            stage={cc.stage}
            initial={
              assessment
                ? {
                    overallSummary: assessment.overallSummary ?? "",
                    strengths: assessment.strengths ?? "",
                    concerns: assessment.concerns ?? "",
                    culturalFitNotes: assessment.culturalFitNotes ?? "",
                    trajectoryNotes: assessment.trajectoryNotes ?? "",
                    riskNotes: assessment.riskNotes ?? "",
                    competencyScores: assessment.competencyScores,
                  }
                : undefined
            }
            assessmentVersionId={assessment?.id ?? null}
            thresholdPassing={assessment?.thresholdPassing ?? null}
            thresholdOverriddenAt={assessment?.thresholdOverriddenAt ?? null}
            thresholdOverrideReason={assessment?.thresholdOverrideReason ?? null}
            thresholdFailures={assessment?.thresholdFailures ?? []}
            onOverrideThreshold={handleOverrideThreshold}
            onClearThresholdOverride={handleClearThresholdOverride}
            onSave={(data) => handleSaveAssessment(cc.id, data)}
            onCancel={() => setAssessingId(null)}
            onSuggestEvidence={handleSuggestEvidence}
            suggestingEvidenceForId={suggestingEvidenceForId}
            saving={savingAssessment}
          />
        </div>
      );
    }

    const canExpand =
      !!assessment && assessment.competencyScores.length > 0 && dimensions.length > 0;

    return (
      <div key={cc.id}>
        {/* biome-ignore lint/a11y/noStaticElementInteractions: role is set conditionally */}
        <div
          role={canExpand ? "button" : undefined}
          tabIndex={canExpand ? 0 : undefined}
          className={`flex items-center gap-3 border-b border-border px-3 py-2${canExpand ? " cursor-pointer hover:bg-card-hover" : ""}`}
          onClick={canExpand ? () => setExpandedId(isExpanded ? null : cc.id) : undefined}
          onKeyDown={
            canExpand
              ? (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setExpandedId(isExpanded ? null : cc.id);
                  }
                }
              : undefined
          }
        >
          <div className="min-w-0 flex-1">
            {typeof cc.candidate.referenceNumber === "number" && (
              <>
                <CandidateReferenceBadge referenceNumber={cc.candidate.referenceNumber} />{" "}
              </>
            )}
            <span className="text-sm font-medium text-text-primary">{cc.candidate.fullName}</span>
            {(cc.candidate.currentRoleTitle || cc.candidate.currentCompany) && (
              <span className="ml-2 text-xs text-text-muted">
                {[cc.candidate.currentRoleTitle, cc.candidate.currentCompany]
                  .filter(Boolean)
                  .join(" at ")}
              </span>
            )}
          </div>

          {assessment ? (
            <>
              {assessment.overallScore != null && (
                <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                  {assessment.overallScore}/5
                </span>
              )}
              {assessment.recommendation && (
                <span
                  className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${RECOMMENDATION_COLORS[assessment.recommendation] ?? ""}`}
                >
                  {RECOMMENDATION_LABELS[assessment.recommendation] ?? assessment.recommendation}
                </span>
              )}
              <ThresholdFlag
                passing={assessment.thresholdPassing}
                overridden={!!assessment.thresholdOverriddenAt}
                overrideReason={assessment.thresholdOverrideReason}
                failures={assessment.thresholdFailures}
                size="sm"
              />
              <span className="text-[10px] text-text-faint">v{assessment.versionNumber}</span>
              {canExpand && (
                <ChevronDown
                  size={14}
                  className={`text-text-faint transition-transform ${isExpanded ? "rotate-180" : ""}`}
                />
              )}
            </>
          ) : (
            <span className="text-xs text-text-faint">Not assessed</span>
          )}

          {assessment && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                openAssessment(cc.id);
              }}
              title="View full assessment"
              className="flex items-center gap-1 rounded px-2 py-1 text-xs text-accent hover:bg-accent/5"
              data-testid={`open-assessment-${cc.id}`}
            >
              View full assessment
            </button>
          )}
          {movedPast ? (
            <div className="flex items-center gap-1">
              <StageBadge stage={cc.stage} />
              <TerminalStageMoveControl
                stage={cc.stage}
                onMove={(stage) => handleMove(cc.id, stage)}
              />
            </div>
          ) : (
            <div className="flex gap-1">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setAssessingId(cc.id);
                }}
                title={assessment ? "Edit assessment" : "Assess"}
                className="flex items-center gap-1 rounded px-2 py-1 text-xs text-accent hover:bg-accent/5"
              >
                <Pencil size={12} /> {assessment ? "Edit" : "Assess"}
              </button>
              <StageDropdown
                currentStage="assessment"
                onMove={(stage) => handleMove(cc.id, stage)}
                onReturnToPool={() => openReturnToPool(cc, "assessment")}
              />
            </div>
          )}
        </div>

        {/* Expanded radar chart view */}
        {isExpanded && assessment && assessment.competencyScores.length > 0 && (
          <div className="border-b border-border bg-gray-50 px-4 py-4 dark:bg-gray-900/20">
            <RadarChart
              dimensions={dimensions}
              candidateScores={buildScoresMap(assessment.competencyScores)}
              candidateName={cc.candidate.fullName}
              size="md"
            />
            <div className="mt-4">
              <ScoreComparisonTable
                dimensions={dimensions}
                candidateName={cc.candidate.fullName}
                candidateScores={buildScoresMap(assessment.competencyScores)}
              />
            </div>
            {assessment.overallSummary && (
              <div className="mt-4">
                <h4 className="text-xs font-medium text-text-muted">Overview</h4>
                <p className="mt-1 whitespace-pre-wrap text-sm text-text-secondary">
                  {assessment.overallSummary}
                </p>
              </div>
            )}
            {assessment.uncoveredBriefFields && assessment.uncoveredBriefFields.length > 0 && (
              <div
                data-testid="assessment-uncovered-brief-fields"
                className="mt-4 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100"
              >
                <span className="font-medium">Assessed without explicit coverage of:</span>{" "}
                {assessment.uncoveredBriefFields.map(labelForBriefField).join(", ")}. These
                populated brief fields were not declared by any capability dimension when the
                assessment ran.
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-2">
        <Link
          to={`/campaigns/${campaignId}/define/assessment`}
          className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs font-medium text-text-secondary hover:bg-card-hover"
          data-testid="view-assessment-matrix-link"
        >
          <Grid3x3 size={14} />
          View assessment matrix
        </Link>
        <Link
          to={`/campaigns/${campaignId}/candidate-scoring-matrix`}
          className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs font-medium text-text-secondary hover:bg-card-hover"
          data-testid="view-candidate-scoring-matrix-link"
        >
          <Grid3x3 size={14} />
          View assessment scores
        </Link>
      </div>

      <AssessmentGuidePanel
        campaignId={campaignId}
        guide={assessmentGuide}
        matrixReady={matrixReady}
        onSaved={refresh}
        defaultCollapsed
      />
      {dimensions.length === 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-400">
          No capability dimensions defined yet. Go to the Role Blueprint stage to add them for
          structured scoring.
        </div>
      )}

      <div className="rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring">
        <div className="mb-3 flex items-center gap-2">
          <ClipboardCheck size={16} className="text-text-faint" />
          <h3 className="text-sm font-medium text-text-secondary">Assessment</h3>
          <span className="text-xs text-text-faint">
            {activeCandidates.length} active
            {movedOnCandidates.length > 0 && `, ${movedOnCandidates.length} moved on`}
          </span>
        </div>

        {error ? (
          <QueryErrorState
            error={error}
            what="assessment candidates"
            onRetry={refresh}
            retrying={fetching}
          />
        ) : allCandidates.length === 0 ? (
          <p className="py-6 text-center text-sm text-text-faint">
            No candidates at assessment stage. Move candidates from Outreach.
          </p>
        ) : (
          <div className="space-y-1">
            {activeCandidates.map((cc) => renderCandidate(cc, false))}
            {movedOnCandidates.map((cc) => renderCandidate(cc, true))}
          </div>
        )}
      </div>
      {assessmentSlideOver}
      {returnToPoolDialog}
    </div>
  );
}
